import { useEffect, useState } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { storage, db } from "../../firebase";
import Toast from "../Toast/Toast";
import "./AdminHomeHeroManager.css";

export default function AdminHomeHeroManager() {
  const [form, setForm] = useState({
    address: "",
    phone: "",
    email: "",
    facebook: "",
  });

  const [notificationEmails, setNotificationEmails] = useState([""]);
  const [emailErrors, setEmailErrors] = useState([]);
  const [preview, setPreview] = useState(null);
  const [currentHeroUrl, setCurrentHeroUrl] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "info") => {
    setToast({ message, type });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const contactSnap = await getDoc(doc(db, "settings", "basicInfo"));
        if (contactSnap.exists()) {
          const data = contactSnap.data();
          setForm({
            address: data.address || "",
            phone: data.phone || "",
            email: data.email || "",
            facebook: data.social?.facebook || "",
          });
          setNotificationEmails(data.notificationEmails || [""]);
        }

        const heroSnap = await getDoc(doc(db, "settings", "heroImage"));
        if (heroSnap.exists()) {
          const { url } = heroSnap.data();
          setCurrentHeroUrl(url || "/hero-bg-square.png");
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleEmailChange = (index, value) => {
    const updated = [...notificationEmails];
    updated[index] = value;
    setNotificationEmails(updated);
  };

  const addEmailField = () => {
    setNotificationEmails([...notificationEmails, ""]);
  };

  const removeEmailField = (index) => {
    const updated = notificationEmails.filter((_, i) => i !== index);
    setNotificationEmails(updated.length ? updated : [""]);
  };

  const validateEmails = () => {
    const errors = notificationEmails.map((email) =>
      /^\S+@\S+\.\S+$/.test(email.trim()) ? "" : "Invalid email"
    );
    setEmailErrors(errors);
    return errors.every((e) => e === "");
  };

  const saveContactInfo = async () => {
    if (!validateEmails()) {
      showToast("Please fix invalid email(s).", "error");
      return;
    }

    try {
      await setDoc(doc(db, "settings", "basicInfo"), {
        ...form,
        notificationEmails: notificationEmails.map((e) => e.trim()),
        social: {
          facebook: form.facebook,
        },
        updatedAt: new Date(),
      });
      showToast("Contact info saved successfully!", "success");
    } catch (err) {
      console.error("Save error:", err);
      showToast("Failed to save contact info.", "error");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreview({
        file,
        url: URL.createObjectURL(file),
      });
    }
  };

  const uploadHeroImage = async () => {
    if (!preview?.file) return;
    const imageRef = ref(storage, "hero-image.jpg");
    try {
      await uploadBytes(imageRef, preview.file);
      const downloadURL = await getDownloadURL(imageRef);
      await setDoc(doc(db, "settings", "heroImage"), {
        url: downloadURL,
        updatedAt: new Date(),
      });
      showToast("Hero image uploaded successfully!", "success");
      setPreview(null);
      setCurrentHeroUrl(downloadURL);
    } catch (error) {
      console.error("Upload error:", error);
      showToast("Failed to upload hero image.", "error");
    }
  };

  const restoreDefaultHero = async () => {
    try {
      await setDoc(doc(db, "settings", "heroImage"), {
        url: "/hero-bg-square.png",
        restoredAt: new Date(),
      });
      showToast("Default image restored!", "success");
      setPreview(null);
      setCurrentHeroUrl("/hero-bg-square.png");
    } catch (error) {
      console.error("Restore error:", error);
      showToast("Failed to restore default image.", "error");
    }
  };

  return (
    <div className="admin-content">
      <h2 className="admin-section-title">Business Contact Info</h2>
      <p className="admin-section-subtitle">
        Update what customers see on the site and where notifications go.
      </p>

      <div className="admin-field-group">
        <label className="admin-field-label">Business Address</label>
        <input
          className="file-input"
          name="address"
          value={form.address}
          onChange={handleChange}
        />
      </div>

      <div className="admin-field-group">
        <label className="admin-field-label">Phone Number</label>
        <input
          className="file-input"
          name="phone"
          value={form.phone}
          onChange={handleChange}
        />
      </div>

      <div className="admin-field-group">
        <label className="admin-field-label">Public Email</label>
        <input
          className="file-input"
          name="email"
          value={form.email}
          onChange={handleChange}
        />
      </div>

      <div className="admin-field-group">
        <label className="admin-field-label">Notification Emails</label>
        {notificationEmails.map((email, index) => (
          <div className="email-row" key={index}>
            <input
              className="file-input email-input"
              value={email}
              onChange={(e) => handleEmailChange(index, e.target.value)}
            />
            {notificationEmails.length > 1 && (
              <button
                className="remove-email-button"
                onClick={() => removeEmailField(index)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
        {emailErrors.some((e) => e) && (
          <ul className="email-error-list">
            {emailErrors.map(
              (error, i) => error && <li key={i}>Email {i + 1}: {error}</li>
            )}
          </ul>
        )}
        <button
          className="upload-button outline add-email-button"
          onClick={addEmailField}
        >
          Add Email
        </button>
      </div>

      <div className="admin-field-group">
        <label className="admin-field-label">Facebook URL</label>
        <input
          className="file-input"
          name="facebook"
          value={form.facebook}
          onChange={handleChange}
        />
      </div>

      <div className="hero-buttons">
        <button onClick={saveContactInfo} className="upload-button">
          Save Contact Info
        </button>
      </div>

      <h2 className="admin-section-title">Homepage Hero Image</h2>
      <p className="admin-section-subtitle">
        Upload a new homepage hero image or restore the default.
      </p>

      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="file-input"
      />

      <div className="hero-preview">
        <img src={preview ? preview.url : currentHeroUrl} alt="Hero preview" />
      </div>

      <div className="hero-buttons">
        {preview && (
          <button onClick={uploadHeroImage} className="upload-button">
            Upload Hero Image
          </button>
        )}
        <button onClick={restoreDefaultHero} className="upload-button outline">
          Restore Default Hero
        </button>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
